/**
 * Suivi environnemental activable par enclos et par zone, typé Terrestre / Bassin.
 *
 * Modèle (document enclosures/{enclosureId}) :
 *
 *   environmentalTracking: {
 *     enabled: boolean,                     // niveau enclos : bloc propre à l'enclos
 *     habitat: 'terrestrial' | 'aquatic',   // type du niveau enclos
 *     zones: { [areaId]: { enabled: boolean } }
 *   }
 *   areas[i].areaType: 'enclosure' | 'basin' // type de la zone : champ EXISTANT
 *                                            // ('enclosure' = Terrestre, 'basin' = Bassin)
 *
 * Le type d'une zone n'est pas dupliqué : c'est `areaType`, déjà édité dans
 * « Zones » et déjà lu par les téléphones pour choisir les paramètres.
 * Le bloc « niveau enclos » garde l'identifiant de zone historique
 * `global_{enclosureId}` : relevés, compteurs et seuils existants s'y rattachent.
 *
 * Legacy (aucune écriture de migration) :
 * - zone sans réglage → suivie, avec son type actuel (`areaType`, absent = Terrestre) ;
 * - enclos sans réglage → niveau enclos suivi s'il n'a pas de zone (le bloc
 *   « Enclos principal » d'aujourd'hui), non suivi s'il a des zones (aujourd'hui
 *   aucun bloc enclos n'accompagne les zones) ; sans type → Terrestre, comme
 *   aujourd'hui. Rien n'apparaît ni ne disparaît tant que rien n'est réglé.
 *
 * Écriture : update ciblé par chemin de champ du seul réglage modifié
 * (`environmentalTracking.enabled`, `.habitat`, `.zones.<areaId>.enabled`), jamais
 * l'objet entier ni le set() complet de la fiche, qui le relit dans le document —
 * cf. SiteDatabaseConnector._setEnclosureEnvironmentalTracking et _setEnclosureAreaType.
 *
 * IMPORTANT : même logique côté mobile
 * (WellfareMonitor2/app/utils/helpers/environmentalTracking.js) — garder synchronisé.
 */

export const ENVIRONMENTAL_HABITATS = Object.freeze({
  TERRESTRIAL: "terrestrial",
  AQUATIC: "aquatic",
});

const HABITAT_VALUES = Object.values(ENVIRONMENTAL_HABITATS);

export const isEnvironmentalHabitat = (value) => HABITAT_VALUES.includes(value);

// Type de zone existant → type de milieu. Tout ce qui n'est pas un bassin
// (dont un areaType absent) est terrestre : c'est le choix des paramètres d'aujourd'hui.
export const areaTypeToHabitat = (areaType) =>
  areaType === "basin" ? ENVIRONMENTAL_HABITATS.AQUATIC : ENVIRONMENTAL_HABITATS.TERRESTRIAL;

export const habitatToAreaType = (habitat) =>
  habitat === ENVIRONMENTAL_HABITATS.AQUATIC ? "basin" : "enclosure";

export const getEnclosureLevelAreaId = (enclosureId) => `global_${enclosureId}`;

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const readTrackingConfig = (enclosure) =>
  isPlainObject(enclosure?.environmentalTracking) ? enclosure.environmentalTracking : {};

const readZoneSetting = (enclosure, areaId) => {
  const zones = readTrackingConfig(enclosure).zones;
  return isPlainObject(zones) && isPlainObject(zones[areaId]) ? zones[areaId] : {};
};

const hasZones = (enclosure) => Array.isArray(enclosure?.areas) && enclosure.areas.length > 0;

/**
 * Réglage effectif d'un niveau de suivi.
 * - resolveEnvironmentalTracking(enclosure) → niveau enclos ;
 * - resolveEnvironmentalTracking(zone, enclosure) → zone (réglage lu dans l'enclos).
 * @returns {{ enabled: boolean, habitat: 'terrestrial'|'aquatic', isConfigured: boolean }}
 *   isConfigured : un réglage d'activation explicite existe en base.
 */
export const resolveEnvironmentalTracking = (target, enclosure = null) => {
  if (enclosure) {
    const setting = readZoneSetting(enclosure, target?.areaId);
    const isConfigured = typeof setting.enabled === "boolean";
    return {
      enabled: isConfigured ? setting.enabled : true,
      habitat: areaTypeToHabitat(target?.areaType),
      isConfigured,
    };
  }
  const config = readTrackingConfig(target);
  const isConfigured = typeof config.enabled === "boolean";
  return {
    enabled: isConfigured ? config.enabled : !hasZones(target),
    habitat: isEnvironmentalHabitat(config.habitat)
      ? config.habitat
      : ENVIRONMENTAL_HABITATS.TERRESTRIAL,
    isConfigured,
  };
};

/**
 * Blocs affichés dans la vue Environnement, au format des zones (areaId,
 * areaName, areaType…) : le niveau enclos s'il est suivi, puis chaque zone suivie.
 * Un niveau désactivé n'a ni paramètres, ni saisie, ni seuils, ni historique à
 * l'écran ; ses relevés restent en base.
 * @param {object|null} enclosure - document enclosures/{id}
 * @param {{ enclosureId?: string, enclosureLevelName?: string }} options
 * @returns {object[]}
 */
export const getEnvironmentalTrackingBlocks = (enclosure, options = {}) => {
  const enclosureId = options.enclosureId || enclosure?.enclosureId;
  const blocks = [];
  const enclosureLevel = resolveEnvironmentalTracking(enclosure);
  if (enclosureLevel.enabled) {
    blocks.push({
      areaId: getEnclosureLevelAreaId(enclosureId),
      areaName: enclosure?.enclosureName || options.enclosureLevelName || "",
      areaType: habitatToAreaType(enclosureLevel.habitat),
      habitat: enclosureLevel.habitat,
      isGlobal: true,
    });
  }
  (hasZones(enclosure) ? enclosure.areas : []).forEach((area) => {
    const zone = resolveEnvironmentalTracking(area, enclosure);
    if (!zone.enabled) return;
    blocks.push({
      ...area,
      areaType: habitatToAreaType(zone.habitat),
      habitat: zone.habitat,
    });
  });
  return blocks;
};

const cleanZones = (zones) => {
  const out = {};
  Object.keys(isPlainObject(zones) ? zones : {}).forEach((areaId) => {
    if (typeof zones[areaId]?.enabled === "boolean") out[areaId] = { enabled: zones[areaId].enabled };
  });
  return out;
};

// Réglage prêt à écrire : uniquement des valeurs définies (Firestore refuse
// `undefined`), seules les clés réellement réglées sont posées.
const buildConfig = ({ enabled, habitat, zones }) => {
  const next = {};
  if (typeof enabled === "boolean") next.enabled = enabled;
  if (isEnvironmentalHabitat(habitat)) next.habitat = habitat;
  const cleanedZones = cleanZones(zones);
  if (Object.keys(cleanedZones).length > 0) next.zones = cleanedZones;
  return next;
};

/** Nouveau réglage après modification du niveau enclos ({ enabled } et/ou { habitat }). */
export const withEnclosureTracking = (environmentalTracking, change = {}) => {
  const current = isPlainObject(environmentalTracking) ? environmentalTracking : {};
  return buildConfig({
    enabled: typeof change.enabled === "boolean" ? change.enabled : current.enabled,
    habitat: isEnvironmentalHabitat(change.habitat) ? change.habitat : current.habitat,
    zones: current.zones,
  });
};

/**
 * Chemins à écrire pour un changement de réglage ({ enabled }, { habitat } ou
 * { zones: { [areaId]: { enabled } } }), relatifs à `environmentalTracking`.
 * Écrire l'objet entier, calculé depuis l'état d'un écran ouvert plus tôt,
 * annulait les réglages faits entre-temps sur un autre écran (désactiver une
 * zone en réactivait une autre) : seuls les réglages changés partent.
 * @returns {Array<[string[], boolean|string]>}
 */
export const getEnvironmentalTrackingFieldUpdates = (change = {}) => {
  const updates = [];
  if (typeof change?.enabled === "boolean") updates.push([["enabled"], change.enabled]);
  if (isEnvironmentalHabitat(change?.habitat)) updates.push([["habitat"], change.habitat]);
  const zones = isPlainObject(change?.zones) ? change.zones : {};
  Object.keys(zones).forEach((areaId) => {
    if (areaId && typeof zones[areaId]?.enabled === "boolean") {
      updates.push([["zones", areaId, "enabled"], zones[areaId].enabled]);
    }
  });
  return updates;
};

/** Nouveau réglage après activation / désactivation d'une zone. */
export const withZoneTracking = (environmentalTracking, areaId, enabled) => {
  const current = isPlainObject(environmentalTracking) ? environmentalTracking : {};
  return buildConfig({
    enabled: current.enabled,
    habitat: current.habitat,
    zones: { ...cleanZones(current.zones), [areaId]: { enabled: enabled === true } },
  });
};
